import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingDown } from "lucide-react";

export default function ConcentrationPage() {
  const [data, setData] = useState(null);
  const [loss, setLoss] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/risk/concentration").then(r => { setData(r.data); setLoading(false); }); }, []);

  const simulateLoss = async (cid) => {
    const { data } = await api.post(`/risk/customer-loss/${cid}`);
    setLoss(data);
  };

  if (loading) return <div>Lade…</div>;

  return (
    <div className="space-y-6" data-testid="concentration-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Kundenkonzentration</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Klumpenrisiko</h1>
        <p className="text-sm text-neutral-500 mt-2">Wie abhängig bist du von einzelnen Kunden? (rollend letzte 12 Monate)</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Top-1-Anteil" value={`${data.top1_share_pct}%`} accent={data.top1_share_pct >= 30 ? "text-red-600" : ""} />
        <Stat label="Top-3-Anteil" value={`${data.top3_share_pct}%`} accent={data.top3_share_pct >= 60 ? "text-red-600" : ""} />
        <Stat label="Anzahl Kunden 12M" value={data.customers.length} />
        <Stat label="Umsatz 12M (netto)" value={fmtCurrency(data.total_revenue_12m)} />
      </div>

      {data.warnings.length > 0 && (
        <div className="space-y-2" data-testid="conc-warnings">
          {data.warnings.map((w, i) => (
            <div key={i} className={`rounded-xl border p-4 flex gap-3 items-start ${w.severity === "red" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
              <AlertTriangle className={`h-5 w-5 shrink-0 mt-0.5 ${w.severity === "red" ? "text-red-600" : "text-amber-600"}`} />
              <div>
                <div className="font-medium">{w.title}</div>
                <div className="text-sm text-neutral-700">{w.message}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3">Kunde</th>
              <th className="text-right px-4 py-3">Umsatz 12M</th>
              <th className="text-right px-4 py-3">Anteil</th>
              <th className="text-right px-4 py-3">Honorar</th>
              <th className="text-right px-4 py-3">davon Media</th>
              <th className="text-right px-4 py-3">Retainer-Anteil</th>
              <th className="text-right px-4 py-3">Rechnungen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.customers.map((c, i) => (
              <tr key={c.customer_id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`conc-row-${i}`}>
                <td className="px-4 py-3 font-medium">{c.company_name}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtCurrency(c.revenue_12m)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-medium" style={{ color: c.revenue_share_pct >= 30 ? "#dc2626" : undefined }}>{c.revenue_share_pct}%</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtCurrency(c.agency_fee_12m)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-neutral-500">{fmtCurrency(c.media_part_12m)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{c.retainer_share_pct}%</td>
                <td className="px-4 py-3 text-right tabular-nums text-neutral-500">{c.invoice_count}</td>
                <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => simulateLoss(c.customer_id)} data-testid={`conc-simulate-${i}`}><TrendingDown className="h-3.5 w-3.5 mr-1" />Was wäre wenn weg?</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loss && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6" data-testid="loss-simulation">
          <div className="text-[11px] uppercase tracking-[0.18em] text-red-700 font-semibold mb-2">Szenario: Was, wenn {loss.company_name} wegfällt?</div>
          <h3 className="font-display text-xl font-semibold mb-4">Auswirkung auf deine Liquidität</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Verlorener Retainer (netto/Monat)" value={fmtCurrency(loss.lost_monthly_retainer_net)} accent="text-red-600" />
            <Stat label="Verlorene offene Forderungen" value={fmtCurrency(loss.lost_open_receivables)} accent="text-red-600" />
            <Stat label="Reichweite vorher" value={loss.runway_before_months ? `${loss.runway_before_months} Monate` : "∞"} />
            <Stat label="Reichweite danach" value={loss.runway_after_months ? `${loss.runway_after_months} Monate` : "∞"} accent="text-red-600" />
          </div>
          <p className="text-sm text-neutral-700 mt-4">
            Wenn {loss.company_name} morgen kündigt: monatliches Defizit von <strong>{fmtCurrency(loss.monthly_deficit_after_loss)}</strong> bei aktuellen Fixkosten.
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-semibold mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}
